import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Phone, MapPin, Package, Plus, Minus, Trash2, ShoppingCart, ChevronDown, LogIn, MessageCircle, Tag, Mail, Truck, Banknote, Star, X } from 'lucide-react';
import { placeConsumerOrder, getMembershipByPhone, createMembershipRequest, createPaymentIntent } from '@/services/firestore';
import type { PlacedOrder } from '@/services/firestore';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { OrderPaymentMethod } from '@/types';
import { computeDeliveryFee, getDeliveryDistanceKm, MPESA_TILL_NUMBER, DELIVERY_BASE_AREA } from '@/lib/delivery';
import { memberKey, isMemberActive, MEMBER_ORDER_DISCOUNT, MEMBER_MONTHLY_PRICE, MEMBER_TILL_NUMBER, LAST_MEMBER_PHONE_KEY } from '@/lib/membership';
import type { Membership } from '@/types';
import { GRADE_IMAGES } from '@/lib/landingImages';
import { loadPricingSettings, getPricePerKg, DEFAULT_PRICING } from '@/components/Admin/PriceManager';
import type { PricingSettings } from '@/components/Admin/PriceManager';

const LAST_TRACKING_KEY = 'gv:lastTrackingCode';
const LAST_TRACKING_PHONE_KEY = 'gv:lastTrackingPhone';
const LAST_HOUSE_KEY = 'gv:lastHouseNumber';
const LAST_ESTATE_KEY = 'gv:lastEstate';
const LAST_ORDER_KEY = 'gv:lastOrder';
const LOYALTY_FREE_DELIVERY_EVERY = 5; // mirrors functions/index.js loyalty rule
const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// Stable phone key — last 9 digits, matches phoneKey() in functions/index.js.
const clientPhoneKey = (raw: string) => {
  const d = raw.replace(/\D/g, '');
  return d.length >= 9 ? d.slice(-9) : null;
};

// â”€â”€â”€ Product catalogue â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface Product {
  id: string;
  type: string;
  size: string;
  label: string;
  desc: string;
  photoUrl?: string;
}

const PRODUCTS: Product[] = [
  { id: 'red-md', type: 'Red', size: 'Medium', label: 'Red - Medium', desc: 'Most popular grade' },
  { id: 'red-sm', type: 'Red', size: 'Small', label: 'Red - Small', desc: 'Best for retail & home use' },
  { id: 'red-lg', type: 'Red', size: 'Large', label: 'Red - Large', desc: 'Great for restaurants & bulk' },
];

const QTY_PRESETS = [25, 50, 100, 200];

const POPULAR_AREAS = ['Gikomba', 'Eastleigh', 'Westlands', 'Umoja', 'Githurai'];
const ALL_AREAS = [
  'Buruburu', 'Dagoretti', 'Eastleigh', 'Embakasi', 'Gigiri', 'Gikomba',
  'Githurai', 'Huruma', 'Industrial Area', 'Kahawa', 'Kasarani', 'Kayole',
  'Kibera', 'Kileleshwa', 'Koinange', 'Komarock', 'Langata', 'Majengo',
  'Mathare', 'Mombasa Road', 'Mukuru', 'Ngong Road', 'Parklands',
  'Pipeline', 'Pumwani', 'Ruai', 'Ruaraka', 'Roysambu',
  'South B', 'South C', 'Umoja', 'Westlands', 'Zimmerman',
];

function formatPhone(raw: string) {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return '+254' + digits.slice(1);
  if (digits.startsWith('254') && digits.length === 12) return '+' + digits;
  return raw;
}

interface CartItem { productId: string; type: string; size: string; label: string; qty: number }

const OrderPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Preselect product from URL (?p=red-md). Falls back to "Red - Medium".
  const initialProductId = (() => {
    const fromUrl = searchParams.get('p');
    return PRODUCTS.find(p => p.id === fromUrl)?.id ?? PRODUCTS[0].id;
  })();

  // Cart state
  const [selectedProductId, setSelectedProductId] = useState(initialProductId);
  const [pendingQty, setPendingQty] = useState(50);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [pricing, setPricing] = useState<PricingSettings>(DEFAULT_PRICING);

  // Repeat-order snapshot (last placed cart, this device) + loyalty progress (by phone)
  const [lastOrderSnap] = useState<{ items: CartItem[]; deliveryLocation?: string } | null>(() => {
    try { const raw = localStorage.getItem(LAST_ORDER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [loyalty, setLoyalty] = useState<{ completedOrders: number; freeDeliveryAvailable: boolean } | null>(null);

  // Delivery
  const [areaSearch, setAreaSearch] = useState('');
  const [areaOpen, setAreaOpen] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [houseNumber, setHouseNumber] = useState(() => {
    try { return localStorage.getItem(LAST_HOUSE_KEY) || ''; } catch { return ''; }
  });
  const [estate, setEstate] = useState(() => {
    try { return localStorage.getItem(LAST_ESTATE_KEY) || ''; } catch { return ''; }
  });
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  // Payment
  const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod>('pay_on_delivery');
  const [mpesaCode, setMpesaCode] = useState('');

  // Membership (Genvitungu Plus)
  const [member, setMember] = useState<Membership | null>(null);
  const [showJoin, setShowJoin] = useState(false);
  const [stkSending, setStkSending] = useState(false);

  // Contact
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [altPhone, setAltPhone] = useState('');
  const [email, setEmail] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [trackingCode, setTrackingCode] = useState('');
  const [placed, setPlaced] = useState<PlacedOrder | null>(null); // server-authored totals

  const emailIsInvalid = email.trim().length > 0 && !isValidEmail(email.trim());

  // Load product photos & pricing from Firestore
  useEffect(() => {
    const fetchProductPhotos = async () => {
      try {
        const photosSnap = await getDocs(collection(db, 'product_photos'));
        const photos: Record<string, string> = {};
        photosSnap.forEach((doc) => {
          photos[doc.id] = doc.data().photoUrl || '';
        });
        setProductPhotos(photos);
      } catch (error) {
        console.error('Error fetching product photos:', error);
      }
    };
    fetchProductPhotos();
    loadPricingSettings().then(setPricing);
  }, []);

  // Recognise a returning Plus member on load (so perks apply before the phone step)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LAST_MEMBER_PHONE_KEY);
      if (saved) getMembershipByPhone(memberKey(saved)).then(m => { if (isMemberActive(m)) setMember(m); }).catch(() => {});
    } catch { /* private mode */ }
  }, []);

  // Look up Genvitungu Plus membership when the phone changes
  useEffect(() => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 9) return; // keep any already-recognised member
    let cancelled = false;
    getMembershipByPhone(memberKey(phone))
      .then(m => {
        if (cancelled) return;
        setMember(m);
        if (isMemberActive(m)) { try { localStorage.setItem(LAST_MEMBER_PHONE_KEY, phone); } catch { /* ignore */ } }
      })
      .catch(() => { if (!cancelled) setMember(null); });
    return () => { cancelled = true; };
  }, [phone]);

  // Loyalty progress for a returning customer (customers/{phoneKey}, written by the
  // onCustomerOrderCreated function). Public-get rule; no PII beyond their own counter.
  useEffect(() => {
    const k = clientPhoneKey(phone);
    if (!k) { setLoyalty(null); return; }
    let cancelled = false;
    getDoc(doc(db, 'customers', k))
      .then(s => {
        if (cancelled || !s.exists()) return;
        const d = s.data();
        setLoyalty({ completedOrders: d.completedOrders || 0, freeDeliveryAvailable: !!d.freeDeliveryAvailable });
      })
      .catch(() => { /* offline / not found — no nudge */ });
    return () => { cancelled = true; };
  }, [phone]);

  const selectedProduct = PRODUCTS.find(p => p.id === selectedProductId)!;

  // Repeat the last order placed on this device — refill the cart in one tap.
  const repeatLastOrder = () => {
    if (!lastOrderSnap?.items?.length) return;
    setCart(lastOrderSnap.items);
    if (lastOrderSnap.deliveryLocation) setDeliveryLocation(lastOrderSnap.deliveryLocation);
  };

  const filteredAreas = useMemo(() =>
    areaSearch.trim()
      ? ALL_AREAS.filter(a => a.toLowerCase().includes(areaSearch.toLowerCase()))
      : ALL_AREAS,
    [areaSearch]);

  const totalKg = cart.reduce((s, i) => s + i.qty, 0);
  const isMember = isMemberActive(member);
  const pricePerKg = totalKg > 0 ? getPricePerKg(totalKg, pricing) : pricing.tier1Price;
  const estimatedTotal = totalKg * pricePerKg;

  // Plus member perks: free delivery + KSh 10 off the order.
  // Loyalty reward: every 5th order earns the next delivery free (mirrors the server flag).
  const loyaltyFreeDelivery = !!loyalty?.freeDeliveryAvailable;
  const deliveryKm = getDeliveryDistanceKm(deliveryLocation);
  const deliveryFee = (isMember || loyaltyFreeDelivery) ? 0 : computeDeliveryFee(deliveryLocation);
  const memberDiscount = isMember && cart.length > 0 ? MEMBER_ORDER_DISCOUNT : 0;
  const grandTotal = Math.max(0, estimatedTotal + deliveryFee - memberDiscount);

  // Bulk-buy nudge: kg needed to reach the next cheaper price tier (UI only).
  const nextTierHint = totalKg <= 0 ? null
    : totalKg < pricing.tier1MaxKg ? { kg: pricing.tier1MaxKg - totalKg, price: pricing.tier2Price }
    : totalKg <= pricing.tier2MaxKg ? { kg: pricing.tier2MaxKg + 1 - totalKg, price: pricing.tier3Price }
    : null;

  const addToCart = () => {
    if (pendingQty < pricing.minOrderKg) return;
    setCart(prev => {
      const existing = prev.find(c => c.productId === selectedProductId);
      if (existing) return prev.map(c => c.productId === selectedProductId ? { ...c, qty: c.qty + pendingQty } : c);
      return [...prev, { productId: selectedProductId, type: selectedProduct.type, size: selectedProduct.size, label: selectedProduct.label, qty: pendingQty }];
    });
  };

  const updateCartQty = (productId: string, delta: number) => {
    setCart(prev => prev.map(c => c.productId === productId
      ? { ...c, qty: Math.max(pricing.minOrderKg, c.qty + delta) }
      : c));
  };

  const removeFromCart = (productId: string) => setCart(prev => prev.filter(c => c.productId !== productId));

  const selectArea = (area: string) => { setDeliveryLocation(area); setAreaOpen(false); setAreaSearch(''); };

  const isValid =
    name.trim().length >= 2 &&
    phone.trim().length >= 9 &&
    !!deliveryLocation &&
    houseNumber.trim().length > 0 &&
    estate.trim().length > 0 &&
    cart.length > 0 &&
    totalKg >= pricing.minOrderKg &&
    !emailIsInvalid;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      // Send only the raw cart + contact/delivery details. The server recomputes
      // every price field authoritatively (createOrder callable) — the client no
      // longer sends or controls the amount.
      const result = await placeConsumerOrder({
        customerName: name.trim(),
        phone: formatPhone(phone.trim()),
        customerAltPhone: altPhone.trim() ? formatPhone(altPhone.trim()) : undefined,
        customerEmail: email.trim() ? email.trim().toLowerCase() : undefined,
        deliveryArea: deliveryLocation,
        houseNumber: houseNumber.trim(),
        estate: estate.trim(),
        deliveryInstructions: deliveryInstructions.trim() || undefined,
        items: cart.map(c => ({ onionType: c.type, onionSize: c.size, quantityKg: c.qty })),
        paymentMethod,
        mpesaConfirmationCode: paymentMethod === 'pay_before' && mpesaCode.trim() ? mpesaCode.trim().toUpperCase() : undefined,
      });
      try {
        localStorage.setItem(LAST_TRACKING_KEY, result.trackingCode);
        localStorage.setItem(LAST_TRACKING_PHONE_KEY, formatPhone(phone.trim()));
        localStorage.setItem(LAST_HOUSE_KEY, houseNumber.trim());
        localStorage.setItem(LAST_ESTATE_KEY, estate.trim());
        localStorage.setItem(LAST_ORDER_KEY, JSON.stringify({ items: cart, deliveryLocation }));
      } catch { /* private mode */ }
      setPlaced(result);
      setTrackingCode(result.trackingCode);
    } catch (err) {
      console.error(err);
      alert('Could not place order. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  // Trigger an M-Pesa STK push for the just-placed order (IntaSend).
  const payNow = async () => {
    setStkSending(true);
    try {
      await createPaymentIntent('order', { trackingCode });
      alert('Check your phone — enter your M-Pesa PIN to complete payment. We confirm automatically.');
    } catch {
      alert('Could not start the M-Pesa prompt. You can still pay to the till shown.');
    } finally {
      setStkSending(false);
    }
  };

  // â”€â”€â”€ Success screen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (trackingCode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center p-5">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={44} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Order Placed!</h2>
          <p className="text-gray-500 text-sm mb-6">
            We'll call you shortly to confirm delivery.
            {email.trim() && !emailIsInvalid && (
              <span className="block mt-1 text-emerald-600 text-xs">A confirmation has been emailed to {email.trim().toLowerCase()}.</span>
            )}
          </p>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-5 mb-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tracking Code</p>
            <p className="text-4xl font-black text-orange-600 tracking-widest">{trackingCode}</p>
            <p className="text-xs text-gray-400 mt-2">Screenshot or write this down</p>
          </div>

          <div className="text-sm text-left space-y-2 bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex justify-between"><span className="text-gray-500">Deliver to</span><span className="font-semibold">{deliveryLocation}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">House no.</span><span className="font-semibold">{houseNumber}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Estate</span><span className="font-semibold text-right">{estate}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total weight</span><span className="font-semibold">{(placed?.totalWeightKg ?? totalKg)} kg</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Delivery fee</span><span className="font-semibold">{(placed?.deliveryFee ?? 0) > 0 ? `KSh ${(placed?.deliveryFee ?? 0).toLocaleString()}` : 'Free'}</span></div>
            {(placed?.discount ?? 0) > 0 && <div className="flex justify-between"><span className="text-gray-500">Plus discount</span><span className="font-semibold text-emerald-700">− KSh {placed?.discount}</span></div>}
            <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-500">Total</span><span className="font-extrabold text-green-700">KSh {(placed?.total ?? grandTotal).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Payment</span><span className="font-semibold text-green-600">{paymentMethod === 'pay_before' ? 'Pay before delivery' : 'Pay on delivery'}</span></div>
          </div>

          {paymentMethod === 'pay_before' && (
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-6 text-left">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pay via M-Pesa Buy Goods</p>
              <p className="text-sm text-gray-700">Till Number <span className="font-black text-green-700 text-lg">{MPESA_TILL_NUMBER}</span></p>
              <p className="text-sm text-gray-700">Amount <span className="font-bold text-green-700">KSh {(placed?.total ?? grandTotal).toLocaleString()}</span></p>
              <p className="text-xs text-gray-500 mt-1">Pay before delivery. We'll confirm receipt and dispatch your order.</p>
              <button onClick={payNow} disabled={stkSending}
                className="mt-3 w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-bold rounded-xl text-sm">
                {stkSending ? 'Sending prompt…' : 'Pay now with M-Pesa'}
              </button>
            </div>
          )}

          <a href={`/track?code=${encodeURIComponent(trackingCode)}&phone=${encodeURIComponent(phone.trim())}`}
            className="block w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors mb-3">
            Track My Order
          </a>
          <button onClick={() => { setTrackingCode(''); setPlaced(null); setCart([]); setName(''); setPhone(''); setAltPhone(''); setEmail(''); setDeliveryLocation(''); setDeliveryInstructions(''); setPaymentMethod('pay_on_delivery'); setMpesaCode(''); }}
            className="w-full py-2.5 text-gray-500 text-sm hover:text-gray-700">
            Place another order
          </button>
        </div>
      </div>
    );
  }

  // â”€â”€â”€ Main order form â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Brand header — aligned with LandingPage editorial theme */}
      <div className="bg-emerald-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-20 shadow-sm">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src="/brand/logo-icon.png" alt="Genvitungu" className="w-8 h-8 rounded-lg object-cover" />
          <div className="leading-none">
            <p className="font-extrabold text-lg">Genvitungu</p>
            <p className="text-emerald-200 text-[10px] uppercase tracking-widest mt-0.5">Red onions, Nairobi</p>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <a href="tel:+254790215267" className="flex items-center gap-1 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors">
            <Phone size={14} /> Call
          </a>
          <Link to="/admin" className="flex items-center gap-1 bg-white text-emerald-900 hover:bg-emerald-50 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors">
            <LogIn size={14} /> <span className="hidden sm:inline">Staff</span>
          </Link>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 pb-32 space-y-5">

        {/* Hero */}
        <div className="text-center py-3">
          <h1 className="text-2xl font-extrabold text-gray-900">Order Fresh Onions</h1>
          <p className="text-gray-500 text-sm mt-1">Nairobi delivery &middot; Pay on delivery &middot; Same or next day</p>
        </div>

        {/* ── Repeat last order (this device) ── */}
        {(lastOrderSnap?.items?.length ?? 0) > 0 && cart.length === 0 && (
          <button onClick={repeatLastOrder}
            className="w-full text-left bg-white border-2 border-orange-200 hover:border-orange-400 rounded-2xl p-4 flex items-center gap-3 transition-colors">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center shrink-0">
              <ShoppingCart size={18} className="text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-sm">Repeat last order</p>
              <p className="text-xs text-gray-500 truncate">{lastOrderSnap!.items.map(i => `${i.qty}kg ${i.label}`).join(' · ')}</p>
            </div>
            <span className="text-orange-600 font-bold text-sm shrink-0">Refill</span>
          </button>
        )}

        {/* ── Loyalty progress nudge ── */}
        {loyalty && !isMember && (
          <div className={`rounded-2xl p-3 text-sm font-semibold flex items-center gap-2 ${loyaltyFreeDelivery ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
            <Star size={16} className="shrink-0" />
            {loyaltyFreeDelivery
              ? 'Free delivery unlocked on this order — thanks for your loyalty!'
              : (() => {
                  const left = LOYALTY_FREE_DELIVERY_EVERY - (loyalty.completedOrders % LOYALTY_FREE_DELIVERY_EVERY);
                  return `${left} order${left === 1 ? '' : 's'} until free delivery`;
                })()}
          </div>
        )}

        {/* ── Pricing tiers banner ── */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Tag size={16} className="text-orange-500" />
            <h2 className="font-bold text-gray-800 text-sm">Price per kg (all orders)</h2>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-white rounded-xl px-2 py-2.5 border border-orange-100">
              <p className="text-gray-500 leading-tight">Under {pricing.tier1MaxKg} kg</p>
              <p className="text-xl font-extrabold text-orange-600 mt-1">{pricing.tier1Price}</p>
              <p className="text-gray-400">KSh/kg</p>
            </div>
            <div className="bg-white rounded-xl px-2 py-2.5 border border-orange-100">
              <p className="text-gray-500 leading-tight">{pricing.tier1MaxKg}–{pricing.tier2MaxKg} kg</p>
              <p className="text-xl font-extrabold text-orange-600 mt-1">{pricing.tier2Price}</p>
              <p className="text-gray-400">KSh/kg</p>
            </div>
            <div className="bg-white rounded-xl px-2 py-2.5 border border-orange-100">
              <p className="text-gray-500 leading-tight">Above {pricing.tier2MaxKg} kg</p>
              <p className="text-xl font-extrabold text-orange-600 mt-1">{pricing.tier3Price}</p>
              <p className="text-gray-400">KSh/kg</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">Minimum order: {pricing.minOrderKg} kg · Pay on delivery</p>
          {/* Bulk-buy nudge — pure UI, surfaces the real tier savings */}
          <p className="text-xs text-center text-emerald-700 font-semibold mt-1.5">
            Buy more, pay less: hit {pricing.tier1MaxKg} kg → save KSh {pricing.tier1Price - pricing.tier2Price}/kg · {pricing.tier2MaxKg}+ kg → save KSh {pricing.tier1Price - pricing.tier3Price}/kg
          </p>
        </div>

        {/* ── Genvitungu Plus ── */}
        {isMember ? (
          <div className="bg-emerald-900 text-white rounded-2xl p-4 flex items-center gap-3">
            <Star size={20} className="text-amber-300 shrink-0" />
            <div>
              <p className="font-bold">Genvitungu Plus member</p>
              <p className="text-emerald-200 text-sm">Free delivery + KSh {MEMBER_ORDER_DISCOUNT} off applied to this order.</p>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowJoin(true)}
            className="w-full text-left bg-gradient-to-r from-emerald-900 to-emerald-700 hover:from-emerald-950 text-white rounded-2xl p-4 flex items-center justify-between gap-3 transition-colors">
            <div className="flex items-center gap-3">
              <Star size={20} className="text-amber-300 shrink-0" />
              <div>
                <p className="font-bold">Genvitungu Plus</p>
                <p className="text-emerald-100 text-sm">Free delivery + KSh {MEMBER_ORDER_DISCOUNT} off every order — KSh {MEMBER_MONTHLY_PRICE}/mo</p>
              </div>
            </div>
            <span className="bg-white text-emerald-900 font-bold text-sm px-4 py-2 rounded-xl shrink-0">Join</span>
          </button>
        )}

        {/* ─── Product selector ─── */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><Package size={17} className="text-orange-500" />Choose Product</h2>
          <div className="space-y-2">
            {PRODUCTS.map(p => {
              const photo = productPhotos[p.id] || GRADE_IMAGES[p.id];
              return (
                <button key={p.id} onClick={() => setSelectedProductId(p.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${selectedProductId === p.id ? 'border-orange-400 bg-orange-50' : 'border-gray-100 hover:border-gray-300 bg-white'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedProductId === p.id ? 'border-orange-500' : 'border-gray-300'}`}>
                    {selectedProductId === p.id && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                  </div>
                  {photo && (
                    <img src={photo} alt={p.label} className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">{p.label}</p>
                    <p className="text-xs text-gray-500">{p.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* â”€â”€ Quantity & Add to cart â”€â”€ */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-3">Quantity (kg)</h2>
          <div className="flex gap-2 mb-3">
            {QTY_PRESETS.map(q => (
              <button key={q} onClick={() => setPendingQty(q)}
                className={`flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all ${pendingQty === q ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}>
                {q} kg
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setPendingQty(q => Math.max(pricing.minOrderKg, q - 5))} className="w-11 h-11 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors">
              <Minus size={18} />
            </button>
            <input type="number" value={pendingQty} min={pricing.minOrderKg} step={5}
              onChange={e => setPendingQty(Math.max(pricing.minOrderKg, parseInt(e.target.value) || pricing.minOrderKg))}
              className="flex-1 text-center text-2xl font-extrabold bg-white text-gray-900 border-2 border-gray-200 rounded-xl py-2 focus:border-orange-400 outline-none" />
            <button onClick={() => setPendingQty(q => q + 5)} className="w-11 h-11 bg-orange-100 hover:bg-orange-200 text-orange-600 rounded-xl flex items-center justify-center transition-colors">
              <Plus size={18} />
            </button>
          </div>
          <button onClick={addToCart}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">
            <ShoppingCart size={18} /> Add {pendingQty} kg of {selectedProduct.label}
          </button>
        </div>

        {/* â”€â”€ Cart â”€â”€ */}
        {cart.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <ShoppingCart size={17} className="text-orange-500" /> Your Cart
              <span className="ml-auto text-sm font-normal text-gray-500">{totalKg} kg total</span>
            </h2>
            <div className="space-y-2">
              {cart.map(item => (
                <div key={item.productId} className="flex items-center gap-3 bg-orange-50 rounded-xl px-3 py-2.5">
                  <span className="text-sm font-semibold text-gray-800 flex-1">{item.label}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateCartQty(item.productId, -5)} className="w-7 h-7 bg-white rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100"><Minus size={13} /></button>
                    <span className="w-14 text-center font-bold text-sm">{item.qty} kg</span>
                    <button onClick={() => updateCartQty(item.productId, 5)} className="w-7 h-7 bg-white rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100"><Plus size={13} /></button>
                  </div>
                  <button onClick={() => removeFromCart(item.productId)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            {/* Price summary */}
            <div className="mt-3 pt-3 border-t border-orange-100 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Rate applied</span>
                <span className="font-bold text-orange-600">KSh {pricePerKg}/kg</span>
              </div>
              {nextTierHint && (
                <p className="text-xs text-emerald-700 font-semibold bg-emerald-50 rounded-lg px-2.5 py-1.5">
                  Add {nextTierHint.kg} kg more to drop to KSh {nextTierHint.price}/kg
                </p>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Goods subtotal</span>
                <span className="font-semibold text-gray-800">KSh {estimatedTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Delivery {deliveryLocation ? (deliveryFee > 0 ? `(${deliveryKm} km from ${DELIVERY_BASE_AREA})` : `(${DELIVERY_BASE_AREA} — free)`) : ''}</span>
                <span className="font-semibold text-gray-800">{!deliveryLocation ? '—' : deliveryFee > 0 ? `KSh ${deliveryFee.toLocaleString()}` : 'Free'}</span>
              </div>
              {memberDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-700">Plus discount</span>
                  <span className="font-semibold text-emerald-700">− KSh {memberDiscount}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-extrabold pt-1">
                <span className="text-gray-800">Estimated Total</span>
                <span className="text-green-700">KSh {grandTotal.toLocaleString()}</span>
              </div>
              <p className="text-xs text-gray-400 text-right">price may vary · delivery KSh 25/km outside {DELIVERY_BASE_AREA}</p>
            </div>
          </div>
        )}
        {/* â"€â"€ Need Help? Contact Us â"€â"€ */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
            <MessageCircle size={17} className="text-green-600" />
            Need Help Ordering?
          </h2>
          <p className="text-xs text-gray-600 mb-3">Have a specific order or question? Chat with us directly!</p>
          
          <div className="space-y-2">
            <a href="https://wa.me/254790215267?text=Hi!%20I%20need%20help%20with%20ordering%20onions"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors shadow-sm">
              <MessageCircle size={18} />
              <div className="flex-1 text-left">
                <div className="text-sm">Chat on WhatsApp</div>
                <div className="text-xs opacity-90">+254 790 215 267</div>
              </div>
            </a>
            
            <a href="tel:+254790215267"
              className="flex items-center gap-3 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold transition-colors shadow-sm">
              <Phone size={18} />
              <div className="flex-1 text-left">
                <div className="text-sm">Call Us Directly</div>
                <div className="text-xs opacity-90">+254 790 215 267</div>
              </div>
            </a>
          </div>
          
          <div className="mt-3 text-xs text-gray-500 text-center">
            Available Mon-Sat, 7AM - 7PM
          </div>
        </div>
        {/* â”€â”€ Delivery location â”€â”€ */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><MapPin size={17} className="text-orange-500" />Delivery Area</h2>

          {/* Popular quick picks */}
          <div className="flex flex-wrap gap-2 mb-3">
            {POPULAR_AREAS.map(a => (
              <button key={a} onClick={() => selectArea(a)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${deliveryLocation === a ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-200 text-gray-600 hover:border-orange-400'}`}>
                {a}
              </button>
            ))}
          </div>

          {/* Searchable dropdown */}
          <div className="relative">
            <button onClick={() => setAreaOpen(o => !o)}
              className={`w-full flex items-center justify-between px-4 py-3 border-2 rounded-xl transition-colors text-left ${deliveryLocation ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}>
              <span className={deliveryLocation ? 'font-semibold text-gray-900' : 'text-gray-400'}>
                {deliveryLocation || 'Search or select area...'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${areaOpen ? 'rotate-180' : ''} text-gray-400`} />
            </button>
            {areaOpen && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-2 border-b">
                  <input autoFocus type="text" placeholder="Type to search..." value={areaSearch}
                    onChange={e => setAreaSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
                </div>
                <div className="max-h-52 overflow-y-auto py-1">
                  {filteredAreas.map(a => (
                    <button key={a} onClick={() => selectArea(a)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors ${deliveryLocation === a ? 'font-semibold text-orange-600' : 'text-gray-700'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* House number + Estate — required for delivery */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">House No. *</label>
              <input type="text" placeholder="e.g. A12" value={houseNumber}
                onChange={e => setHouseNumber(e.target.value)}
                autoCapitalize="characters" maxLength={12}
                className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estate *</label>
              <input type="text" placeholder="e.g. Kilimani Highrise" value={estate}
                onChange={e => setEstate(e.target.value)}
                autoCapitalize="words" maxLength={60}
                className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
            </div>
          </div>

          {deliveryLocation && (
            <div className="mt-3">
              <textarea placeholder="Delivery instructions (e.g. near Equity Bank, call on arrival...)"
                value={deliveryInstructions} onChange={e => setDeliveryInstructions(e.target.value)}
                rows={2} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-orange-400 outline-none resize-none mt-1 bg-white text-gray-900" />
            </div>
          )}
        </div>

        {/* â”€â”€ Contact details â”€â”€ */}
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
          <h2 className="font-bold text-gray-800 flex items-center gap-2"><Phone size={17} className="text-orange-500" />Your Contact</h2>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name *</label>
            <input type="text" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)}
              className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone Number *</label>
            <input type="tel" placeholder="07XX XXX XXX" value={phone} onChange={e => setPhone(e.target.value)}
              className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Alternative Phone (optional)</label>
            <input type="tel" placeholder="07XX XXX XXX" value={altPhone} onChange={e => setAltPhone(e.target.value)}
              className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Mail size={12} /> Email (optional) <span className="font-normal normal-case text-gray-400 text-[10px]">— for receipt & status updates</span>
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={`mt-1 w-full px-4 py-3 border-2 rounded-xl outline-none bg-white text-gray-900 ${
                emailIsInvalid ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-orange-400'
              }`}
            />
            {emailIsInvalid && <p className="text-xs text-red-500 mt-1">Please enter a valid email or leave blank.</p>}
          </div>
        </div>

        {/* ── Payment method ── */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><Banknote size={17} className="text-orange-500" />Payment</h2>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setPaymentMethod('pay_on_delivery')}
              className={`flex flex-col items-start gap-1 px-4 py-3 rounded-xl border-2 text-left transition-all ${paymentMethod === 'pay_on_delivery' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <Truck size={18} className={paymentMethod === 'pay_on_delivery' ? 'text-orange-500' : 'text-gray-400'} />
              <span className="font-semibold text-sm text-gray-900">Pay on delivery</span>
              <span className="text-xs text-gray-500">Cash / M-Pesa to rider</span>
            </button>
            <button type="button" onClick={() => setPaymentMethod('pay_before')}
              className={`flex flex-col items-start gap-1 px-4 py-3 rounded-xl border-2 text-left transition-all ${paymentMethod === 'pay_before' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <Banknote size={18} className={paymentMethod === 'pay_before' ? 'text-orange-500' : 'text-gray-400'} />
              <span className="font-semibold text-sm text-gray-900">Pay before delivery</span>
              <span className="text-xs text-gray-500">M-Pesa Buy Goods</span>
            </button>
          </div>

          {paymentMethod === 'pay_before' && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Buy Goods Till</span>
                <span className="font-black text-green-700 text-lg tracking-wide">{MPESA_TILL_NUMBER}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Amount to pay</span>
                <span className="font-bold text-green-700">KSh {grandTotal.toLocaleString()}</span>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">M-Pesa code (optional)</label>
                <input type="text" placeholder="e.g. SDJ7XK2P9Q" value={mpesaCode}
                  onChange={e => setMpesaCode(e.target.value)}
                  autoCapitalize="characters" maxLength={15}
                  className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none bg-white text-gray-900" />
                <p className="text-xs text-gray-500 mt-1">Pay to the till, then enter the M-Pesa code. You can also pay after placing the order — we'll confirm before dispatch.</p>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* â”€â”€ Sticky bottom CTA â”€â”€ */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-2xl px-4 py-3">
        <div className="max-w-lg mx-auto">
          {!isValid && (
            <p className="text-center text-xs text-gray-400 mb-2">
              {cart.length === 0 ? `Add at least ${pricing.minOrderKg} kg to your cart` :
               totalKg < pricing.minOrderKg ? `Minimum order is ${pricing.minOrderKg} kg` :
               !deliveryLocation ? 'Select a delivery area' :
               houseNumber.trim().length === 0 ? 'Enter your house number' :
               estate.trim().length === 0 ? 'Enter your estate' :
               name.trim().length < 2 ? 'Enter your name' :
               phone.trim().length < 9 ? 'Enter your phone number' : ''}
            </p>
          )}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-xs text-gray-500">Your order</p>
              <p className="font-extrabold text-gray-900">
                {totalKg > 0 ? `${totalKg} kg` : '--'}
                {grandTotal > 0 && <span className="text-orange-600 font-bold ml-1 text-sm">· KSh {grandTotal.toLocaleString()}</span>}
                {' '}<span className="text-xs font-normal text-green-600">{paymentMethod === 'pay_before' ? 'Pay before delivery' : 'Pay on delivery'}</span>
              </p>
            </div>
            <button onClick={handleSubmit} disabled={!isValid || submitting}
              className="flex-shrink-0 px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-extrabold rounded-xl transition-colors text-base">
              {submitting ? 'Placing...' : 'Place Order'}
            </button>
          </div>
        </div>
      </div>

      {/* Unobtrusive staff portal link */}
      <div className="text-center pb-36 pt-2">
        <Link to="/admin" className="text-xs text-gray-300 hover:text-gray-500 transition-colors">Staff Portal</Link>
      </div>

      {showJoin && (
        <JoinPlusModal
          defaultName={name}
          defaultPhone={phone}
          onClose={() => setShowJoin(false)}
        />
      )}
    </div>

  );
};

// ── Genvitungu Plus join modal ───────────────────────────────────────────────
const JoinPlusModal: React.FC<{ defaultName: string; defaultPhone: string; onClose: () => void }> = ({ defaultName, defaultPhone, onClose }) => {
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState(defaultPhone);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (name.trim().length < 2 || digits.length < 9) { alert('Enter your name and phone.'); return; }
    setSaving(true);
    try {
      const key = memberKey(phone);
      await createMembershipRequest(key, {
        phone: formatPhone(phone.trim()),
        name: name.trim(),
      });
      // Fire the M-Pesa STK push via a payment intent; Plus activates only once payment confirms.
      try {
        await createPaymentIntent('membership', { membershipKey: key });
      } catch { /* customer can still pay to the till manually */ }
      setDone(true);
    } catch (e) {
      console.error(e);
      alert('Could not submit. Please try again or call us.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><Star size={18} className="text-amber-400" /> Genvitungu Plus</h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {done ? (
          <div className="py-4 text-center">
            <CheckCircle size={44} className="text-green-600 mx-auto mb-3" />
            <p className="font-bold text-gray-900">Check your phone 📱</p>
            <p className="text-sm text-gray-600 mt-2">We've sent an M-Pesa prompt for <span className="font-bold">KSh {MEMBER_MONTHLY_PRICE}</span> — enter your PIN to pay. (No prompt? Pay to Buy Goods till <span className="font-bold">{MEMBER_TILL_NUMBER}</span>.)</p>
            <p className="text-xs text-gray-500 mt-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">Plus turns on <span className="font-semibold">only after we receive your payment</span> — verified automatically with M-Pesa. Closing this won't activate it.</p>
            <button onClick={onClose} className="mt-5 w-full py-3 bg-emerald-900 text-white font-bold rounded-xl">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">Free delivery on every order + the best per-kg price regardless of size, for <span className="font-semibold">KSh {MEMBER_MONTHLY_PRICE}/month</span>.</p>
            <div className="space-y-3">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-white text-gray-900" />
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07XX XXX XXX"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-white text-gray-900" />
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Monthly</span><span className="font-bold text-green-700">KSh {MEMBER_MONTHLY_PRICE}</span></div>
                <p className="text-xs text-gray-500 mt-1">Tap below — we'll send an M-Pesa prompt to your phone to confirm.</p>
              </div>
            </div>
            <button onClick={submit} disabled={saving} className="mt-4 w-full py-3 bg-emerald-900 hover:bg-emerald-950 disabled:bg-gray-300 text-white font-bold rounded-xl">
              {saving ? 'Submitting…' : 'Join Plus'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderPage;
